import { supabase } from '../lib/supabase'

export class SupabaseFunctionsService {
  /**
   * Register a user using Supabase Edge Function
   */
  static async registerUser(registrationData) {
    try {
      const { data, error } = await supabase.functions.invoke('register-user', {
        body: { registrationData }
      })

      if (error) {
        throw new Error(error.message || 'Registration failed')
      }

      return {
        success: true,
        user: data.user,
        registration: data.registration,
        message: data.message
      }
    } catch (error) {
      console.error('Registration error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Process payment using Supabase Edge Function
   */
  static async processPayment(paymentData) {
    try {
      const { data, error } = await supabase.functions.invoke('process-payment', {
        body: { paymentData }
      })

      if (error) {
        throw new Error(error.message || 'Payment processing failed')
      }

      return {
        success: true,
        payment: data.payment,
        registration: data.registration,
        user: data.user,
        message: data.message
      }
    } catch (error) {
      console.error('Payment processing error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Create payment intent using Supabase Edge Function
   */
  static async createPaymentIntent(paymentIntentData) {
    try {
      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: { paymentIntentData }
      })

      if (error) {
        throw new Error(error.message || 'Payment intent creation failed')
      }

      return {
        success: true,
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
        amount: data.amount,
        currency: data.currency,
        status: data.status
      }
    } catch (error) {
      console.error('Payment intent creation error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Get retreat details using Supabase Edge Function
   */
  static async getRetreat(retreatId) {
    try {
      const { data, error } = await supabase.functions.invoke('get-retreat', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch retreat')
      }

      return {
        success: true,
        retreat: data.retreat,
        message: data.message
      }
    } catch (error) {
      console.error('Get retreat error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Get all retreats using Supabase Edge Function
   */
  static async getAllRetreats() {
    try {
      const { data, error } = await supabase.functions.invoke('get-retreats', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch retreats')
      }

      return {
        success: true,
        retreats: data.retreats,
        count: data.count,
        message: data.message
      }
    } catch (error) {
      console.error('Get retreats error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Submit contact message using Supabase Edge Function
   */
  static async submitContactMessage(contactData) {
    try {
      const { data, error } = await supabase.functions.invoke('submit-contact-message', {
        body: contactData
      })

      if (error) {
        throw new Error(error.message || 'Contact message submission failed')
      }

      return {
        success: true,
        message: data.message,
        contactMessage: data.data
      }
    } catch (error) {
      console.error('Contact message submission error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Complete registration and payment process using Supabase functions
   */
  static async completeRegistrationAndPayment(userData, retreatId, paymentMethod) {
    try {
      // Step 1: Register user first
      const registrationResult = await this.registerUser({
        ...userData,
        retreatId: retreatId
      })

      if (!registrationResult.success) {
        throw new Error(registrationResult.error)
      }

      // Step 2: Create payment intent
      const paymentIntentResult = await this.createPaymentIntent({
        amount: registrationResult.registration.retreat.price,
        currency: 'USD',
        registrationId: registrationResult.registration.id,
        retreatId: retreatId,
        userEmail: userData.email
      })

      if (!paymentIntentResult.success) {
        throw new Error(paymentIntentResult.error)
      }

      // Step 3: Process payment
      const paymentResult = await this.processPayment({
        registrationId: registrationResult.registration.id,
        amount: registrationResult.registration.retreat.price,
        currency: 'USD',
        paymentMethodId: paymentMethod.id,
        stripePaymentIntentId: paymentIntentResult.paymentIntentId
      })

      if (!paymentResult.success) {
        throw new Error(paymentResult.error)
      }

      return {
        success: true,
        user: registrationResult.user,
        registration: paymentResult.registration,
        payment: paymentResult.payment,
        retreat: registrationResult.registration.retreat,
        message: 'Registration and payment completed successfully'
      }
    } catch (error) {
      console.error('Complete registration and payment error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
}

export default SupabaseFunctionsService
